interface ProjectLike {
  _id: string;
  name: string;
  appCode?: string;
}

/**
 * Shared table-cell chip for a record's owning project (appId).
 * - Resolves appId → project name from the caller's accessible projects.
 * - Renders a grey "Global" pill when the record has no project (appId null).
 *
 * A single-line, truncating chip (not a wrapping Tag): in a narrow/fixed-width
 * Project column a long "Name (CODE)" label would wrap a Tag into a tall blob,
 * so this keeps it on one line with an ellipsis + a hover tooltip.
 *
 * Use in any project-scoped list's "Project" column so every list shows the
 * scope the same way:  body: (row) => <ProjectCell appId={row.appId} projects={accessibleProjects} />
 */
export function ProjectCell({ appId, projects }: { appId?: string | null; projects: ProjectLike[] }) {
  if (!appId) {
    return (
      <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200 dark:bg-gray-700 dark:text-slate-300 dark:ring-gray-600">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
        Core
      </span>
    );
  }
  const p = projects.find((x) => x._id === appId);
  /* An unresolved id used to render as the word "Project", which reads like a
     real label and hides the two things that actually cause it: a project the
     caller cannot see, or an appId that matches no project at all (an import
     carrying a placeholder id, say). Name the state and put the id in the
     tooltip so it can be chased. */
  const label = p ? (p.appCode ? `${p.name} (${p.appCode})` : p.name) : 'Unknown project';
  return (
    <span
      title={p ? label : `Unknown project — appId ${appId}`}
      className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800"
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
      <span className="truncate">{label}</span>
    </span>
  );
}

export default ProjectCell;
